import { GetterTree } from 'vuex';
import i18n from '@/i18n';
import { RestaurantState, RootState } from '../types';
import { state as cartState } from '../cart/cart';
import { state as authState } from '../auth/auth';
import { generateAvailableFilters } from '@/utils/helpers';
import { getCountryFromAddress, getCurrencyFromCountry, getCurrencySymbol } from '@/utils/currency';

export const getters: GetterTree<RestaurantState, RootState> = {
	getRestaurant(state): Restaurant {
		return state.restaurant;
	},

	getCountry(state): string | null {
		return getCountryFromAddress(state.restaurant.address);
	},

	getCurrency(state, getters): string {
		return getCurrencyFromCountry(getters.getCountry);
	},

	getCurrencySymbol(state, getters): string {
		return getCurrencySymbol(getters.getCurrency, i18n.locale);
	},

	getRestaurantName(state): string {
		return state.restaurant && state.restaurant.name;
	},

	getRestaurantVoucher(state): object | undefined {
		return state.restaurant && state.restaurant.voucher;
	},

	getRestaurantExtraPaymentMethods(state): RestaurantExtraPaymentMethods[] | null | undefined {
		return state.restaurant && state.restaurant.extraPaymentMethods;
	},

	getRestaurantDiscount(state): boolean {
		return state.restaurant.hasDiscounts;
	},

	getRestaurantTipConfig(state): object | undefined {
		return state.restaurant && state.restaurant.tipConfig;
	},

	getMaxItemsPerCart(state): number | null {
		return state.restaurant && state.restaurant.maxItemsPerCart;
	},

	getMaxMealsPerCart(state): number | null {
		return state.restaurant && state.restaurant.maxMealsPerCart;
	},

	getMinMealsPerCart(state): number | null {
		return state.restaurant && state.restaurant.minMealsPerCart;
	},

	getSupportEmail(state): string | null {
		return state.restaurant && state.restaurant.supportEmail;
	},

	getRestaurantDeliveryInfo(state): DefaultDelivery | StadiumDelivery | CustomDelivery {
		return state.restaurant && state.restaurant.delivery!;
	},

	getRestaurantMembershipCustomization(state): MembershipCustomization {
		return state.restaurant && state.restaurant.membershipCustomization!;
	},

	getRestaurantMembershipCustomizationLoadingMessage(state): string | undefined {
		return state.restaurant && state.restaurant.membershipCustomization && state.restaurant.membershipCustomization.loading_message;
	},

	getRestaurantPaymentProcessor(state): string {
		return state.restaurant && state.restaurant.payment_processor;
	},

	getRestaurantGroupLocations(state): GroupLocation[] {
		return state.groupLocations;
	},

	getRestaurantPaymentConfig(state): PaymentConfig | null {
		return state.paymentConfig;
	},

	getInitialMenuGroups(state): MenuGroup[] {
		return state.initialMenuGroups;
	},

	getInitialMenus(state): Menu[] {
		return state.initialMenus;
	},

	getFilteredMenus(state): Menu[] {
		return state.filteredMenus;
	},

	getMemberMenus(state): Menu[] {
		return state.memberMenus;
	},

	getRotationalMenus(state): RotationalMenu[] {
		return state.rotationalMenus;
	},

	getTenantId(state): string {
		return state.restaurant && state.restaurant.id;
	},

	getGenericTableLocations(state): GenericTableLocations | null {
		return state.restaurant && state.restaurant.genericTableLocations;
	},

	getConcourseUrl(state): string | null {
		return state.restaurant && state.restaurant.concourse;
	},

	getAvailableDiets(state): string[] {
		let output: string[] = [];
		state.itemList.forEach(item => {
			output = generateAvailableFilters(output, 'diets', item);
		});
		return output;
	},

	getAvailableAllergens(state): string[] {
		let output: string[] = [];
		state.itemList.forEach(item => {
			output = generateAvailableFilters(output, 'allergens', item);
		});
		return output;
	},

	hideItemsSpecialInstructions(state): boolean {
		return state.restaurant && state.restaurant.hide_items_special_instructions;
	},

	hideTakeoutSpecialInstructions(state): boolean {
		return state.restaurant && state.restaurant.hide_takeout_special_instructions;
	},

	isLoyaltyProgramOn(state): string {
		return state.restaurant && state.restaurant.loyalty_program;
	},

	isMembershipProgramOn(state): string[] | string {
		return state.restaurant && state.restaurant.membership_programs?.length
			? state.restaurant.membership_programs
			: '';
	},

	isPosIntegrated(state): boolean {
		return state.restaurant && state.restaurant.pos_integrated;
	},

	isRallyIntelligenceOn(state): boolean {
		return state.restaurant && state.restaurant.rally_intelligence;
	},

	isOpenTab(state): boolean {
		return state.restaurant && state.restaurant.allow_open_tab;
	},

	noTakeoutTimeslot(state): boolean {
		return state.restaurant.noTakeoutTimeslot;
	},

	isAvsRequired(state): boolean {
		return state.restaurant && state.restaurant.avs;
	},

	isMarketplaceHub(state): boolean {
		return state.restaurant && state.restaurant.marketplaceHub;
	},

	hasSubsidies(state): boolean {
		return state.restaurant.subsidiesEnabled && state.restaurant.subsidies && !!state.restaurant.subsidies.length;
	},

	isSubsidyPayWhatYouCan(state, getters): boolean {
		if (!getters.hasSubsidies) {
			return false;
		}
		return state.restaurant.subsidies.some(subsidy =>
			subsidy.is_active &&
			subsidy.type === 'pay_what_you_can' &&
			(subsidy.target_users === 'all' || (subsidy.target_users === 'codes' && authState.authenticated && !!authState.user?.subsidyCode))
		);
	},

	isMultipleMids(state): boolean {
		return state.restaurant && state.restaurant.multipleMids;
	},

	getCustomQuestions(state): CustomQuestion[] {
		return state.restaurant.customQuestions || [];
	},

	getHideGuestInfo(state): HideGuestInfo | undefined {
		return state.restaurant?.hideGuestInfo;
	},

	getZone(state): string | undefined {
		return state.restaurant?.zone;
	},

	getConfirmationScreenCustomization(state): ConfirmationScreenCustomization | undefined {
		return state.restaurant?.confirmationScreenCustomization;
	},

	isOrderItemAgainEnabled(state): boolean | undefined {
		return state.restaurant?.isOrderAgain;
	},

	showSplitTaxes(state): boolean {
		return state.restaurant?.showSplitTaxes;
	},

	orderingRequiresLogin(state): boolean {
		return (state.restaurant.k12 && state.restaurant.k12RequiresLogin) || (!!cartState.config.genericCatering && state.restaurant.genericCateringRequiresLogin);
	},

	getRestaurantSso(state): Sso | null {
		return state.restaurant?.sso || null;
	},

	isK12Location(state): boolean {
		return state.restaurant?.k12;
	},

	isK12MonthlyLayoutEnabled(state): boolean {
		return !!state.restaurant?.k12MonthlyLayout;
	},

	getCalendarViewMode(state): CalendarViewMode {
		return state.calendarViewMode;
	},

	isMonthlyView(state): boolean {
		return !!state.restaurant?.k12MonthlyLayout && state.calendarViewMode === 'monthly';
	},

	hasOrdersRepeat(state): boolean {
		return state.restaurant?.repeat_orders;
	},

	isTaxInclusive(state): boolean {
		return !!state.restaurant?.taxInclusive;
	},

	isTaxInclusiveWithInfo(state): boolean {
		return state.restaurant?.taxInclusive === 'tax_inclusive_with_info';
	},

	getDateRange(state): DateRange {
		return state.rotationalMenuDateRange;
	},

	getOrderCutoffDate(state): DateTimeType | null {
		return state.restaurant?.orderCutoffDate;
	},

	getMaxOrderDate(state): DateTimeType | undefined {
		return state.restaurant?.maxOrderDate;
	},

	hasWeekendOrdering(state): boolean {
		return state.restaurant?.weekendOrdering || false;
	},

	getRotationalMenusFetching(state): boolean {
		return state.rotationalMenusFetching;
	},

	getRepeatingOrdersMenus(state): RotationalMenu[] {
		return state.repeatingOrdersMenus;
	},

	getRepeatingOrdersUnavailableDates(state): string[] {
		return state.repeatingOrdersUnavailableDates;
	},

	getRepeatingOrdersSelectedMealType(state): string {
		return state.repeatingOrdersSelectedMealType;
	},

	genericCateringHideCreditCard(state): boolean {
		return state.restaurant?.genericCateringHideCreditCard || false;
	},

	genericCateringShowPickupOption(state): boolean {
		return state.restaurant?.genericCateringShowPickupOption || false;
	},

	getAnnouncements(state): Announcement[] {
		return state.announcements;
	},

	getLogoRedirectUrl(state): string | null {
		return state.restaurant?.logoRedirectUrl || null;
	},

	getNutritionalInfoUrl(state): string | null {
		return state.restaurant?.nutritionalInfoUrl || null;
	},

	hasCssLoaded(state): boolean {
		return state.cssLoaded;
	},

	hidePricing(state): boolean {
		return state.restaurant?.hidePricing || false;
	},

	getK12TitleDisplay(state): string | null {
		return state.restaurant?.k12TitleDisplay || null;
	},

	hideTakeoutTime(state): boolean {
		return state.restaurant?.hideTakeoutTime || false;
	},

	modifyRequestsEnabled(state): boolean {
		return state.restaurant.modifyOrderRequests;
	},

	getInquiryForm(state): InquiryForm | null {
		return state.inquiryForm;
	}
};